// SlideContent.js
import React, { useRef, useState } from 'react';
import './Slidecontent.css';
import './FinalSlide.css';
import Twemoji from 'react-twemoji'; 
import html2canvas from 'html2canvas';
import TransparentLogo from './transparent_logo.png';
import { renderHorizontalBarChart, renderHashtagBarChart, renderEmojiBarChartNoPercent } from './graphs';

const monthNames = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
};

function SlideContent({ data, slideIndex }) {
  // This ref now targets only the part of the slide to be captured for download.
  const finalSlideRef = useRef(null);
  const [colorScheme, setColorScheme] = useState('black'); // default scheme

  if (!data) {
    return <div>Loading...</div>;
  }

  const userItem = data?.wrappedData?.Item;
  const {
    username,
    followers_count,
    bio,
    bio_roast,
    profile_pic,
    architypes,
    post_types,
    image_labels,
    monthly_posts,
    image_roast,
    top_cringiest_posts,
    comments_analysis,
    themes,
    top_hashtags,
    hashtag_descriptions,
    top_emojis,
    emojis_description,
  } = userItem || {};

  const downloadFinalSlide = async () => {
    if (finalSlideRef.current) {
      const canvas = await html2canvas(finalSlideRef.current, {
        scale: window.devicePixelRatio * 2,
        useCORS: true,
        imageTimeout: 15000,
        backgroundColor: null,
      });
      canvas.toBlob((blob) => {
        const dataURL = canvas.toDataURL('image/png');
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isAndroid = /Android/.test(userAgent);
  
        if ((isIOS || isAndroid) && navigator.share) {
          // Use the Web Share API to open the native share sheet
          const file = new File([blob], 'sizzlewrap.png', { type: blob.type });
          navigator.share({
            files: [file],
            title: 'SizzleWrap',
            text: 'Check out my SizzleWrap!',
          }).catch((error) => console.error('Error sharing', error));
        } else if (isIOS || isAndroid) {
          // Fallback for mobile devices if navigator.share isn't available:
          window.open(dataURL, '_blank');
        } else {
          // Default behavior for non-mobile devices:
          const link = document.createElement('a');
          link.download = 'sizzlewrap.png';
          link.href = dataURL;
          link.click();
        }
      });
    }
  };

  const creativeArchetype = architypes?.Creative_Architype;
  const emoji = architypes?.Emoji;
  const season = architypes?.Season;
  const artistic_style = architypes?.Artistic_Style;
  const animal = architypes?.Animal;
  const food = architypes?.Food;
  const themeSynopsis = themes?.Synopsis;

  const bestComment = comments_analysis?.Best_Comment;
  const worstComment = comments_analysis?.Worst_Comment;

  let slideComponent;

  switch (slideIndex) {
    case 0: {
      slideComponent = (
        <div className="slide-content" style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div className="first-slide-content" style={{ position: 'relative', zIndex: 1 }}>
            <h1 className="sizzle-heading">
              Welcome to your Instagram SizzleWrap 🔥 {username}!
            </h1>
            <img src={profile_pic} alt="Profile" className="profile-picture" />
            <h3 className="username">{username}</h3>
            <p className="followers">Followers: {followers_count}</p>
          </div>
        </div>
      );
      break;
    }
    
    case 1:
      slideComponent = (
        <div className="slide-content">
          <h1 className="sizzle-heading">Your profile at a glance</h1>
          <h3>Because first impressions matter, right?</h3>
          <br />
          <img src={profile_pic} alt="Profile" className="profile-picture" />
          <p className="bio">{bio}</p>
          <p className="bio-roast">{bio_roast}</p>
        </div>
      );
      break;

    case 2:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            Now let's dive deeper into who you are
          </h1>
        </div>
      );
      break;

    case 3:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">Your Creative Archetype</h2>
          {creativeArchetype ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">
                {creativeArchetype.Value}
              </h3>
              <p className="creative-archetype-explanation">
                {creativeArchetype.Explanation}
              </p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;

    case 4:
      slideComponent = (
        <div className="slide-content">
          <h1 style={{ fontSize: '2rem', margin: '2rem 0' }}>
            Let's look at what you are posting
          </h1>
        </div>
      );
      break;

    case 5:
      slideComponent = (
        <div className="graph-slide-container">
          {renderHorizontalBarChart(
            post_types,
            'Your Top Post Types',
            'You really like posting, huh?',
            true
          )}
        </div>
      );
      break;

    case 6:
      slideComponent = (
        <div className="graph-slide-container">
          {renderHorizontalBarChart(
            image_labels,
            'What We Detected in Your Images',
            'Should we be concerned?',
            true
          )}
        </div>
      );
      break;

    case 7:
      slideComponent = (
        <div className="graph-slide-container">
          {renderHorizontalBarChart(
            monthly_posts,
            "Finally, here's when you posted over the last year",
            '',
            false,
            (key, count) => {
              const [year, mm] = key.split('-');
              const monthName = monthNames[mm] || mm;
              return `${monthName} ${year} (${count})`;
            }
          )}
        </div>
      );
      break;

    case 8:
      slideComponent = (
        <div className="slide-content">
          <h2 className="image-analysis-heading">
            That was too easy... Time for a roast
          </h2>
          <p className="image-roast-text">{image_roast}</p>
        </div>
      );
      break;

    case 9:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            Now let's really roast your posts
          </h1>
        </div>
      );
      break;

    case 10:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            But before we get too spicy, let’s analyze what we think your main post themes are.
          </h1>
        </div>
      );
      break;

    case 11:
      if (!themes || !themes.Themes) {
        slideComponent = (
          <div className="slide-content no-scroll-slide">
            <h2 className="themes-heading">Top Themes</h2>
            <p>No theme data available.</p>
          </div>
        );
      } else {
        const { Themes } = themes;
        slideComponent = (
          <div className="slide-content no-scroll-slide">
            <div className="no-scroll-slide-content">
              <h2 className="themes-heading">
                Your Instagram's Top Themes
              </h2>
              <div className="themes-list">
                {Themes.map((themeObj, idx) => (
                  <div key={idx} className="theme-item">
                    <h3 className="theme-title">{themeObj.Theme}</h3>
                    <p className="theme-explanation">{themeObj.Explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      break;

    case 12: {
      const bestPost = top_cringiest_posts?.Best_Post;
      if (!bestPost) {
        slideComponent = (
          <div className="slide-content best-post-slide">
            <h2>Your Best Post</h2>
            <p>No best post data found.</p>
          </div>
        );
      } else {
        slideComponent = (
          <div className="slide-content best-post-slide">
            <h2 className="best-post-heading">Your Best Post</h2>
            <div className="best-post-wrapper">
              <figure className="best-post-figure">
                <img
                  src={bestPost.URL}
                  alt="Best Post"
                  className="best-post-image"
                />
                <figcaption className="best-post-caption">
                  {bestPost.Caption || "A glimpse of your best post"}
                </figcaption>
              </figure>
              <p className="best-post-explanation">
                {bestPost.Explanation}
              </p>
            </div>
          </div>
        );
      }
      break;
    }

    case 13: {
      const cringiestPost = top_cringiest_posts?.Cringiest_Post;
      if (!cringiestPost) {
        slideComponent = (
          <div className="slide-content best-post-slide">
            <h2>Your Cringiest Post</h2>
            <p>No cringiest post data found.</p>
          </div>
        );
      } else {
        slideComponent = (
          <div className="slide-content best-post-slide">
            <h2 className="best-post-heading">Your Cringiest Post</h2>
            <div className="best-post-wrapper">
              <figure className="best-post-figure">
                <img
                  src={cringiestPost.URL}
                  alt="Cringiest Post"
                  className="best-post-image"
                />
                <figcaption className="best-post-caption">
                  {cringiestPost.Caption || "A glimpse of your cringiest post"}
                </figcaption>
              </figure>
              <p className="best-post-explanation">
                {cringiestPost.Explanation}
              </p>
            </div>
          </div>
        );
      }
      break;
    }

    case 14:
      slideComponent = (
        <div className="slide-content synopsis-slide">
          <h2 className="synopsis-heading">What Your Posts Say About You</h2>
          {themeSynopsis ? (
            <p className="theme-synopsis">{themeSynopsis}</p>
          ) : (
            <p>No synopsis data available.</p>
          )}
        </div>
      );
      break;

    case 15:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            Now let's dive into your comments section.
          </h1>
        </div>
      );
      break;

    case 16:
      if (!bestComment) {
        slideComponent = (
          <div className="slide-content">
            <h2>Your Best Comment</h2>
            <p>No best comment data found.</p>
          </div>
        );
      } else {
        slideComponent = (
          <div className="slide-content">
            <h2>Your Best Comment</h2>
            <div className="comment-wrapper">
              <p className="comment-text">"{bestComment.Comment}"</p>
              <p className="comment-explanation">{bestComment.Explanation}</p>
            </div>
          </div>
        );
      }
      break;

    case 17:
      if (!worstComment) {
        slideComponent = (
          <div className="slide-content">
            <h2>Your Worst Comment</h2>
            <p>No worst comment data found.</p>
          </div>
        );
      } else {
        slideComponent = (
          <div className="slide-content">
            <h2>Your Worst Comment</h2>
            <div className="comment-wrapper">
              <p className="comment-text">"{worstComment.Comment}"</p>
              <p className="comment-explanation">{worstComment.Explanation}</p>
            </div>
          </div>
        );
      }
      break;

    case 18:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            Now let's look at your hashtag "Hall Of Shame"
          </h1>
        </div>
      );
      break;
    
    case 19:
      slideComponent = (
        <div className="graph-slide-container">
          {renderHashtagBarChart(top_hashtags, 'Your Most Used Hashtags', true)}
          {hashtag_descriptions && (
            <p className="hashtag-description" style={{ marginTop: '1rem' }}>
              {hashtag_descriptions}
            </p>
          )}
        </div>
      );
      break;

    case 20:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            Enter Your Emoji Breakdown 🤪😛👻
          </h1>
        </div>
      );
      break;

    case 21:
      slideComponent = (
        <div className="graph-slide-container">
          {renderEmojiBarChartNoPercent(top_emojis, 'Your Most Used Emojis', true)}
          {emojis_description && (
            <p className="hashtag-description" style={{ marginTop: '1rem' }}>
              {emojis_description}
            </p>
          )}
        </div>
      );
      break;

    case 22:
      slideComponent = (
        <div className="slide-content">
          <h1 className="deeper-dive-heading">
            That was fun, wasn't it?
            <br /><br />
            Now let’s wrap things up by hearing what our brilliant AI has to say about you (drumroll, please)...
          </h1>
        </div>
      );
      break;
    
    case 23:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">If You Were an Emoji</h2>
          {emoji ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">
                <Twemoji options={{ className: 'twemoji' }}>
                  {emoji.Value}
                </Twemoji>
              </h3>
              <p className="creative-archetype-explanation">
                {emoji.Explanation}
              </p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;
    
    case 24:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">If You Were an Animal</h2>
          {animal ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">{animal.Value}</h3>
              <p className="creative-archetype-explanation">{season.Explanation}</p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;
    
    case 25:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">If You Were a Season</h2>
          {season ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">{season.Value}</h3>
              <p className="creative-archetype-explanation">{season.Explanation}</p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;
    
    case 26:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">
            If You Were an Artististic Style
          </h2>
          {artistic_style ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">{artistic_style.Value}</h3>
              <p className="creative-archetype-explanation">
                {artistic_style.Explanation}
              </p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;
    
    case 27:
      slideComponent = (
        <div className="slide-content creative-archetype-slide">
          <h2 className="creative-archetype-heading">If You Were a Food</h2>
          {food ? (
            <div className="creative-archetype-info">
              <h3 className="creative-archetype-value">{food.Value}</h3>
              <p className="creative-archetype-explanation">
                {food.Explanation}
              </p>
            </div>
          ) : (
            <p>No creative archetype data found.</p>
          )}
        </div>
      );
      break;
    
    case 28:
      slideComponent = (
        <div className="slide-content">
          <h1 className="sizzle-heading">Thats a wrap!</h1>
          <h3>We hope you had as much fun as we did, {username} !</h3>
          <br />
          <img src={profile_pic} alt="Profile" className="profile-picture" />
          <p className="bio">Until next time, you filthy {animal?.Value} !</p>
        </div>
      );
      break;

    case 29:
      slideComponent = (
        <div className="slide-content final-slide">
          {/* Downloadable canvas with dynamic color scheme */}
          <div
            className={`downloadable-canvas scheme-${colorScheme}`}
            ref={finalSlideRef}
          >
            <h1 className="instagram-snapshot-header">Your Instagram Snapshot</h1>
            <div className="highlights-grid">
              {/* Top Row: Season, Animal, Emoji, Food */}
              <div className="highlight-item">
                <h3>Season</h3>
                {season ? (
                  <p className="highlight-text">{season.Value}</p>
                ) : (
                  <p>No Season Data</p>
                )}
              </div>
              <div className="highlight-item">
                <h3>Animal</h3>
                {animal ? (
                  <p className="highlight-text">{animal.Value}</p>
                ) : (
                  <p>No Animal Data</p>
                )}
              </div>
              <div className="highlight-item">
                <h3>Emoji</h3>
                {emoji ? (
                  <p className="highlight-emoji">
                    <Twemoji options={{ className: 'twemoji' }}>
                      {emoji.Value}
                    </Twemoji>
                  </p>
                ) : (
                  <p>No Emoji Data</p>
                )}
              </div>
              <div className="highlight-item">
                <h3>Food</h3>
                {food ? (
                  <p className="highlight-text">{food.Value}</p>
                ) : (
                  <p>No Food Data</p>
                )}
              </div>

              {/* Bottom Row: Best Post, Cringiest Post */}
              <div className="highlight-item">
                <h3>Best Post</h3>
                {top_cringiest_posts && top_cringiest_posts.Best_Post ? (
                  <img
                    src={top_cringiest_posts.Best_Post.URL}
                    alt="Best Post"
                    className="highlight-image"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <p>No Best Post</p>
                )}
              </div>
              <div className="highlight-item">
                <h3>Cringiest Post</h3>
                {top_cringiest_posts && top_cringiest_posts.Cringiest_Post ? (
                  <img
                    src={top_cringiest_posts.Cringiest_Post.URL}
                    alt="Cringiest Post"
                    className="highlight-image"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <p>No Cringiest Post</p>
                )}
              </div>
            </div>
            <img src={TransparentLogo} alt="Logo" className="final-logo" />
          </div>

          {/* Color scheme switcher */}
          <div className="color-switcher">
            <div
              className={`color-circle ${colorScheme === 'black' ? 'selected' : ''}`}
              style={{ backgroundColor: '#000' }}
              onClick={() => setColorScheme('black')}
              title="Black"
            ></div>
            <div
              className={`color-circle ${colorScheme === 'pink' ? 'selected' : ''}`}
              style={{ backgroundColor: '#ffc0cb' }}
              onClick={() => setColorScheme('pink')}
              title="Light Pink"
            ></div>
            <div
              className={`color-circle ${colorScheme === 'blue' ? 'selected' : ''}`}
              style={{ backgroundColor: '#add8e6' }}
              onClick={() => setColorScheme('blue')}
              title="Light Blue"
            ></div>
          </div>

          {/* Download container */}
          <div className="download-container">
            <button onClick={downloadFinalSlide} className="download-btn">
              Share
            </button>
          </div>
        </div>
      );
      break;

    default:
      slideComponent = (
        <div className="slide-content">
          <h2>Slide Not Found</h2>
          <p>Please check your slide index or data structure.</p>
        </div>
      );
      break;
  }

  return (
    <div className="slide-container">
      {slideComponent}
    </div>
  );
}

export default SlideContent;
